import { z } from "zod";

// ── Value schema ────────────────────────────────────────────

export const ValueSchema = z.object({
  name: z.string(),
  description: z.string(),
  priority: z.enum(["critical", "high", "medium", "low"]).default("medium"),
  details: z.string().optional(),
});

export type Value = z.infer<typeof ValueSchema>;

// ── Budget schema ───────────────────────────────────────────

export const BudgetSchema = z.object({
  monthly_limit: z.number().positive(),
  alert_threshold: z.number().min(0).max(1).default(0.8),
  prefer_batch: z.boolean().default(false),
  currency: z.string().default("USD"),
});

export type Budget = z.infer<typeof BudgetSchema>;

// ── Guardrails schema ───────────────────────────────────────

export const GuardrailsSchema = z.object({
  security: z
    .object({
      templates: z.array(z.string()).default([]),
      custom: z.array(z.string()).default([]),
    })
    .optional(),
  quality: z.array(z.string()).default([]),
  process: z.array(z.string()).default([]),
});

export type Guardrails = z.infer<typeof GuardrailsSchema>;

// ── Tech schema ─────────────────────────────────────────────

export const TechSchema = z.object({
  stack: z.array(z.string()).default([]),
  opinions: z.array(z.string()).default([]),
});

export type Tech = z.infer<typeof TechSchema>;

// ── Providers schema ────────────────────────────────────────

export const ProvidersSchema = z.object({
  preferred: z.string().optional(),
  api_key_env: z.string().regex(/^[A-Z_][A-Z0-9_]*$/, {
    message: "Environment variable name must match ^[A-Z_][A-Z0-9_]*$ (uppercase letters, digits, underscores)",
  }).optional(),
  fallback: z.string().optional(),
  local_models: z.array(z.string()).default([]),
});

export type Providers = z.infer<typeof ProvidersSchema>;

// ── Intent schema (top-level) ───────────────────────────────

export const IntentSchema = z.object({
  project: z.string(),
  description: z.string(),
  audience: z.string().optional(),
  values: z.array(ValueSchema).default([]),
  budget: BudgetSchema.optional(),
  guardrails: GuardrailsSchema.optional(),
  tech: TechSchema.optional(),
  providers: ProvidersSchema.optional(),
});

export type IntentConfig = z.infer<typeof IntentSchema>;

// ── Guardrail template schema ───────────────────────────────

export const GuardrailTemplateSchema = z.object({
  name: z.string(),
  description: z.string(),
  category: z.enum(["security", "quality"]),
  status: z.string().optional(),
  rules: z.array(z.string()),
});

export type GuardrailTemplate = z.infer<typeof GuardrailTemplateSchema>;

// ── Resolved guardrails (flat output) ───────────────────────

export interface ResolvedGuardrails {
  security: string[];
  quality: string[];
  process: string[];
}

export interface TemplateInfo {
  name: string;
  description: string;
  category: string;
  status?: string;
}
